// The live transcript plus the operator's own reply box — the Conversation tab of
// a project in FULL mode.
//
// Answering as Mira is the write that lives here; parking the agent and taking the
// client over live in the page header (`operator-controls.tsx`), because those two
// apply to the whole project rather than to this tab. What happens here:
//
//   - **Send as Mira** (POST .../operator-message). The client sees an ordinary
//     assistant reply; only this thread badges it as operator-sent. Sending also
//     resolves the waiting / asked-for-a-human / frustration flags, because a
//     human just answered.
//   - **Snippets.** Shared canned replies, INSERTED into the draft (never sent on
//     their own) so the operator still reads what goes out.
//
// Live updates are Pusher spectate, never polling (doctrine 1.2): any event on
// the owner's channel debounces into one REST refetch, and REST stays
// authoritative. A failed subscribe degrades to a static thread the page's own
// reads still refresh — the auth endpoint is the CS-gated one, so an operator
// without `customer_support` simply does not get live.
//
// Support mode renders the read-only thread instead, never this:
// `web/routes/admin_support.py` is read-only by design, because speaking to a
// client as Mira is a policy decision about who may be the product's voice.
import { Button, Composer, Menu, useToast, usePusherChannel } from "@recruiter/shared";
import type { SupportChatMessage } from "@recruiter/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError, createSavedReply, postOperatorMessage, type SavedReply } from "../lib/api";
import { adminPusherConfig } from "../lib/pusher";
import { adminKeys, savedRepliesQuery } from "../lib/queries";
import { TranslatableConversation } from "./translatable-conversation";
import "./operator-conversation.css";

/** Long enough that a burst of stream events costs ONE refetch, short enough that
 *  an operator mid-conversation does not notice the lag. */
const REFETCH_DEBOUNCE_MS = 700;

/** A snippet's title, derived from its own first line. The backend caps titles at
 *  120 characters; an operator who wants a different one edits it where snippets
 *  are managed (admin-internal), rather than being prompted mid-takeover. */
function snippetTitle(body: string): string {
  const firstLine = body.trim().split("\n")[0].trim();
  return firstLine.length > 120 ? `${firstLine.slice(0, 117)}...` : firstLine;
}

function toastDetail(error: unknown): string | undefined {
  return error instanceof ApiError ? error.detail : undefined;
}

export function OperatorConversation({
  projectId,
  channel,
  agentPaused,
  messages,
  clientName,
  onChanged,
}: {
  projectId: string;
  /** The owner's private Pusher channel, or null when the project has no owner
   *  to spectate (a guest whose identity has been erased). */
  channel: string | null;
  agentPaused: boolean;
  messages: SupportChatMessage[];
  clientName: string;
  /** Re-read the project over REST. Called after every write and on every live
   *  event, because the fetched record is the source of truth, not our state. */
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState("");
  const toast = useToast();
  const queryClient = useQueryClient();
  const snippets = useQuery(savedRepliesQuery);

  // Spectate the client's own channel. `onReconnect` re-reads after a drop:
  // Pusher does not replay what it published while the socket was down.
  const events = usePusherChannel<unknown>(channel, ["agent.stream"], adminPusherConfig, onChanged);
  useEffect(() => {
    if (events.length === 0) return;
    const timer = setTimeout(onChanged, REFETCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [events.length, onChanged]);

  const send = useMutation({
    mutationFn: (text: string) => postOperatorMessage(projectId, text),
    onSuccess: () => {
      setDraft("");
      toast.add({ title: "Sent to the client", data: { tone: "success" } });
      onChanged();
    },
    onError: (error: unknown) => {
      // The draft is deliberately kept so nothing an operator typed is lost.
      toast.add({
        title: "Couldn't send that reply",
        description: toastDetail(error),
        data: { tone: "danger" },
      });
    },
  });

  const saveSnippet = useMutation({
    mutationFn: (body: string) => createSavedReply(snippetTitle(body), body),
    onSuccess: () => {
      toast.add({ title: "Snippet saved", data: { tone: "success" } });
      void queryClient.invalidateQueries({ queryKey: adminKeys.savedReplies });
    },
    onError: (error: unknown) => {
      toast.add({
        title: "Couldn't save that snippet",
        description: toastDetail(error),
        data: { tone: "danger" },
      });
    },
  });

  const trimmed = draft.trim();
  const replies: SavedReply[] = snippets.data?.replies ?? [];

  function insert(reply: SavedReply) {
    setDraft((current) => (current.trim() ? `${current.trimEnd()}\n${reply.body}` : reply.body));
  }

  return (
    <div className="oc">
      <TranslatableConversation projectId={projectId} messages={messages} clientName={clientName} />

      <div className="oc__composer">
        <Composer
          size="sm"
          minRows={2}
          placeholder={
            agentPaused
              ? `Reply to ${clientName} as Mira`
              : `Reply as Mira (the agent is still active and may also answer)`
          }
          aria-label="Reply to the client as Mira"
          value={draft}
          disabled={send.isPending}
          onChange={(event) => setDraft(event.target.value)}
          submitOnEnter
          onSubmit={() => {
            if (trimmed) send.mutate(trimmed);
          }}
        />
        <div className="oc__composer-side">
          <Button
            variant="solid"
            tone="accent"
            size="sm"
            disabled={send.isPending || trimmed.length === 0}
            onClick={() => send.mutate(trimmed)}
          >
            {send.isPending ? "Sending" : "Send as Mira"}
          </Button>
          <div className="oc__snippets">
            {replies.length > 0 ? (
              <Menu.Root>
                <Menu.Trigger
                  render={
                    <Button variant="ghost" tone="neutral" size="sm">
                      Snippets
                    </Button>
                  }
                />
                <Menu.Content>
                  {replies.map((reply) => (
                    <Menu.Item key={reply.id} onClick={() => insert(reply)}>
                      {reply.title}
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Root>
            ) : null}
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              disabled={trimmed.length === 0 || saveSnippet.isPending}
              title="Save this draft as a shared snippet"
              onClick={() => saveSnippet.mutate(trimmed)}
            >
              Save as snippet
            </Button>
          </div>
          {!agentPaused ? (
            <p className="oc__warn">The agent is live. Consider pausing first.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
